import * as React from 'react';
import {Query} from 'react-apollo';
import {Alert, Integration, Settings} from '../generated/graphql';
import {Graph} from '../types/graphql';

import {AccessOptions} from '../util/permissions';
import {ENTITY_QUERY} from './users';
import makeComp from '../util/profiler';
import {captureError} from '../util/integrations';

///// Simple type helpers
// Type T minus the single key K
type Omit<T, K> = Pick<T, Exclude<keyof T, K>>;
// Type T minus all keys in K
type Subtract<T, K> = Omit<T, keyof K>;

export interface MemberData {
  id: string;
  admin: boolean;
  pending: boolean;
  username: string;
  name: string;
  email: string;
  photoUrl: string;
  accountType: string;
  apiKey: string;
}

export interface EntityData {
  id: string;
  name: string;
  available: boolean;
  photoUrl: string;
  readOnly: boolean;
  storageBytes: number;
  readOnlyAdmin: boolean;
  isTeam: boolean;
  privateOnly: boolean;
  codeSavingEnabled: boolean;
  settings: Settings;
  defaultAccess: AccessOptions;
  members: MemberData[];
  integrations: Graph<Integration>;
  defaultAlerts: Alert[];
  isPaid?: boolean;
  rateLimits?: {
    filestream: number;
    graphql: number;
  };
}

interface UserData {
  id: string;
  username: string;
  accountType: string;
}

export interface Variables {
  name: string;
}

export interface Data {
  entity: EntityData;
  user: UserData;
}

///// HOC definition

// These are the extra props required by the HOC. All are passed through to your
// wrapped component.
interface InputProps {
  match: {
    params: {
      entityName: string;
    };
  };
}

// We define two types of query results, one for the loading state and one for
// the loaded state. This way the consumer can safely check the loading prop once.
// If it's false project is guaranteed to be defined.
interface QueryResultLoadingProps {
  loading: true;
  entity: undefined;
  user: undefined;
  refetch: undefined;
}

interface QueryResultLoadedProps {
  loading: false;
  entity: EntityData;
  user: UserData;
  refetch(): void;
}

// The props that will be injected into your component, as a result of the query.
// They are derived from the result of the query in the HOC.
export interface TeamQueryResultProps {
  teamQuery: QueryResultLoadedProps | QueryResultLoadingProps;
}

export const withTeamQuery = <P extends object>(
  Component: React.ComponentType<P & TeamQueryResultProps>
) =>
  makeComp(
    (inputProps: InputProps) => {
      const {match} = inputProps;
      const variables = {
        name: match.params.entityName,
      };
      return (
        <Query<Data, Variables>
          query={ENTITY_QUERY}
          variables={variables}
          fetchPolicy="cache-and-network">
          {rawQueryResult => {
            const r: TeamQueryResultProps = {
              teamQuery: {
                loading: true,
                entity: undefined,
                user: undefined,
                refetch: undefined,
              },
            };
            if (!rawQueryResult.loading) {
              if (rawQueryResult.data == null) {
                captureError(
                  'Unexpected Apollo error, loading: false, data: undefined',
                  'teamQuery',
                  {extra: {rawQueryResult}}
                );
                throw new Error(
                  'Unexpected Apollo error, loading: false, data: undefined'
                );
              }
              r.teamQuery = {
                loading: false,
                entity: rawQueryResult.data.entity,
                user: rawQueryResult.data.user,
                refetch: rawQueryResult.refetch,
              };
            }
            return <Component {...(inputProps as P)} {...r} />;
          }}
        </Query>
      );
    },
    {id: 'withTeamQuery'}
  );
